import {
	Outlet,
	Route,
	Routes,
	useLocation,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { QuotesGridList } from './views/QuotesGridList';
import { MaterialCountsCosts } from './components/Sidebar/MaterialCountsCosts';
import { QuoteFetchWrapper } from './components/QuoteFetchWrapper';
import { Pricing } from './views/Pricing/Pricing';
import { OptionSelection } from './views/OptionSelection/OptionSelection';
import { ProposalPage } from './views/ProposalPage';
import {
	QuoteStateProvider,
	useQuoteState,
	useQuoteDispatch,
} from '../../stateProviders/QuoteStateProvider';
import { DeckContext, DeckProvider } from './views/blocks/context/DeckContext';
import {
	DeckContext as DeckContextV3,
	DeckProvider as DeckProviderV3,
} from './views/blocks/context/DeckContextV3';
import React, { useContext, useMemo, useState } from 'react';
import { ConfigContext } from '../../base-ui/lib';
import Role from '../../enums/role';
import { useGlobalState } from '../../stateProviders/GlobalStateProvider';
import BreadcrumSimpleChevron from '../../uiFoundationsKit/components/kit/application/navigation/BreadcrumbSimpleChevron.tsx';
import { ProposalPreview } from './views/Preview/indxes.tsx';
import { Stack, Box, Typography, Button, IconButton } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { TbArrowRight } from 'react-icons/tb';
import { EditQuotationModal } from './modalContent/EditQuotationModal';
import { useApi } from '../../hooks/useApi';
import removeBlockRefs from '../../utils/removeBlockRefs';
import { toastError, toastSuccess, toastWarning } from '../../Toaster/Toast';
import QuoteStatus from '../../enums/quoteStatus';
import { OptionSelectionV3 } from './views/OptionSelection/OptionSelectionV3.tsx';
import { useCanvasFullscreenToggle } from 'src/hooks/useCanvasFullscreenToggle.ts';
import validateBlockEdges from 'src/utils/validateBlockEdges.ts';
import useIsBetaCanvasEnabled from 'src/hooks/useBetaCanvas.ts';
import { DesignSpanProvider } from './views/blocks/context/DesignSpanContext.tsx';

const DeckProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const isBetaEnabled = useIsBetaCanvasEnabled();
	const CurrentDeckProvider = isBetaEnabled ? DeckProviderV3 : DeckProvider;
	return <CurrentDeckProvider>{children}</CurrentDeckProvider>;
};

const OptionSelectionWrapper = () => {
	const isBetaEnabled = useIsBetaCanvasEnabled();
	return isBetaEnabled ? <OptionSelectionV3 /> : <OptionSelection />;
};

const QuoteLayout = () => {
	const { config }: any = useContext(ConfigContext);
	const { appUser, company } = useGlobalState();

	let pricingContainerRef;
	let deckState;

	const isBetaEnabled = useIsBetaCanvasEnabled();

	if (isBetaEnabled) {
		const context = useContext(DeckContextV3);

		pricingContainerRef = context.pricingContainerRef;

		deckState = context.state;
	} else {
		const context = useContext(DeckContext);

		pricingContainerRef = context.pricingContainerRef;
		deckState = context.state;
	}

	const quoteState = useQuoteState();
	const quoteDispatch = useQuoteDispatch();
	const { quoteId } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const api = useApi();
	const [editModalOpen, setEditModalOpen] = useState(false);
	const { isCanvasFullscreen } = useCanvasFullscreenToggle();

	const isOptionSelectionPage =
		location.pathname.includes('/options') ||
		(!location.pathname.includes('/pricing') &&
			!location.pathname.includes('/proposal') &&
			!location.pathname.includes('/preview') &&
			quoteId);
	const isPricingPage = location.pathname.includes('/pricing');

	const status = quoteState.currentQuote?.status;
	const isDisabled =
		status &&
		[QuoteStatus.PROPOSALS_SENT, QuoteStatus.PROPOSALS_SIGNED].includes(status);

	const validateQuote = () => {
		// Validate Timbertech Advantage railing type has rail cap style selected
		const hasInvalidWestbury = quoteState.options.some(
			(option) =>
				option.deckingType?.trim().length &&
				option.railingType?.includes('Westbury Tuscany') &&
				!option.railCapStyle?.trim().length
		);

		// Validate Timbertech Advantage railing type has rail cap style selected
		const hasInvalidTimbertechAdvantage = quoteState.options.some(
			(option) =>
				option.deckingType?.trim().length &&
				option.railingType?.includes('Timbertech Advantage') &&
				!option.railCapStyle?.trim().length
		);

		if (hasInvalidWestbury) {
			toastError({
				title: 'Validation Error',
				description:
					'Westbury Tuscany railing type requires a rail cap style to be selected',
			});
			return false;
		}

		if (hasInvalidTimbertechAdvantage) {
			toastError({
				title: 'Validation Error',
				description:
					'Timbertech Advantage railing type requires a rail cap style to be selected',
			});
			return false;
		}
		// Check 1: Validate railing type is selected when decking type is selected
		const hasNoRailingType = quoteState.options.some(
			(option) =>
				option.deckingType?.trim().length && !option.railingType?.trim().length
		);
		if (hasNoRailingType) {
			toastError({
				title: 'Validation Error',
				description:
					'No railing type option selected. Railing Type option is required',
			});
			return false;
		}
		// Validate IRX railing type has rail cap style selected
		const hasInvalidIRX = quoteState.options.some(
			(option) =>
				option.deckingType?.trim().length &&
				option.railingType === 'IRX' &&
				!option.railCapStyle?.trim().length
		);
		if (hasInvalidIRX) {
			toastError({
				title: 'Validation Error',
				description:
					'IRX railing type requires a rail cap style to be selected',
			});
			return false;
		}
		// Check 3: Validate at least one option is fully configured
		if (
			!quoteState.options.some(
				(option) =>
					option.deckingType?.trim().length &&
					option.railingType?.trim().length &&
					(option.railingType !== 'IRX' || option.railCapStyle?.trim().length)
			)
		) {
			toastError({
				title: 'Validation Error',
				description: 'At least one option in Step 3 must be fully configured',
			});

			return false;
		}

		if (!deckState.blocks.length) {
			toastError({
				title: 'Validation Error',
				description: 'No blocks selected yet',
			});

			return false;
		}

		const { error, errorTitle } = validateBlockEdges(deckState);

		if (error || errorTitle) {
			toastError({
				title: errorTitle,
				description: error,
			});

			return false;
		}

		return true;
	};

	const saveQuote = async () => {
		if (!quoteId) return;

		const updatedQuote = await api.quotations.updateQuotation(quoteId, {
			blocks: removeBlockRefs(deckState.blocks),
			materialCost: quoteState.materialCost,
			demoSquareFootage: quoteState.demoSquareFootage,
			demoHeight: quoteState.demoHeight,
			selectedPermit: quoteState.selectedPermit,
			engineeringFeeRequired: quoteState.engineeringFeeRequired,
			septicFeeRequired: quoteState.septicFeeRequired,
			miscMaterialCosts: quoteState.miscMaterialCosts,
			miscLaborCosts: quoteState.miscLaborCosts,
			selectedOptionIndex: quoteState.selectedOptionIndex,
			appliedDiscounts: quoteState.appliedDiscounts,
			options: quoteState.options,
			deckboxHeightInFeet: quoteState.deckboxHeightInFeet,
			requiresFascia: quoteState.requiresFascia,
			reface: quoteState.reface,
			structure: quoteState.structure,
			managerDiscount: quoteState.managerDiscount,
			permitRequired: quoteState.permitRequired,
			designPackage: quoteState.designPackage,
		});

		if (!updatedQuote.errored) {
			toastSuccess({
				title: 'Quote Updated',
				description: 'You have successfully updated the quote details.',
			});
		}
		const quote = await api.quotations.getQuotationById(quoteId);
		quoteDispatch({ ...quote, currentQuote: quote });
	};

	return (
		<Box display="flex" flexDirection="column" height="100%">
			{!isCanvasFullscreen && (
				<>
					{(config.environmentName != 'Production' ||
						appUser?.role?.roleName == Role.SUPER_ADMIN ||
						appUser?.role?.roleName == Role.COMPANY_ADMIN ||
						config.pricingPanelAllowedUsers?.includes(appUser?.email)) && (
						<MaterialCountsCosts />
					)}

					{/* Header - Sticky on non-pricing pages, static on pricing page */}
					<Box
						sx={{
							position: isPricingPage ? 'static' : 'sticky',
							top: 0,
							backgroundColor: 'background.default',
							zIndex: isPricingPage ? 'auto' : 20,
							pt: 1,
							pb: 2,
							pr: 5,
							borderBottom: '1px solid',
							borderColor: 'divider',
							mb: 3,
						}}
					>
						<Stack spacing={2}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								spacing={2}
							>
								<BreadcrumSimpleChevron
									items={[
										{
											label: 'Option Selection',
											href: 'options',
										},
										{
											label: 'View Pricing',
											href: 'pricing',
										},
									]}
								/>

								{isPricingPage && (
									<Box>
										<Box ref={pricingContainerRef} />
									</Box>
								)}
							</Stack>

							{!isPricingPage && (
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="space-between"
									spacing={1}
								>
									<Stack direction="row" spacing={1} alignItems="center">
										{quoteState?.projectName && (
											<Typography variant="h1" fontWeight={700}>
												{quoteState.projectName}
											</Typography>
										)}
										<IconButton
											size="small"
											aria-label="Edit Quotation Details"
											onClick={() => setEditModalOpen(true)}
											color="primary"
										>
											<Edit />
										</IconButton>
										<EditQuotationModal
											open={editModalOpen}
											onClose={() => setEditModalOpen(false)}
											quotation={quoteState as any}
											setQuotation={(updatedQuote) => {
												quoteDispatch(updatedQuote);
											}}
										/>
									</Stack>

									{isOptionSelectionPage && (
										<Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
											{isDisabled ? (
												<Button
													variant="contained"
													color="primary"
													endIcon={<TbArrowRight />}
													onClick={() =>
														navigate(`/quotations/${quoteId}/pricing`)
													}
												>
													View Pricing
												</Button>
											) : (
												<>
													<Button
														variant="outlined"
														color="secondary"
														onClick={async () => {
															if (validateQuote()) {
																await saveQuote();
															}
														}}
													>
														Save
													</Button>
													<Button
														variant="contained"
														color="success"
														endIcon={<TbArrowRight />}
														onClick={async () => {
															if (validateQuote()) {
																await saveQuote();
																const errors = [...quoteState.drawingErrors];

																if (errors.length > 0) {
																	errors.forEach((error) =>
																		toastWarning({
																			title: 'Validation Warning',
																			description: error,
																		})
																	);
																	return;
																}
																navigate(`/quotations/${quoteId}/pricing`, {
																	state: { recalculatePricing: true },
																});
															}
														}}
													>
														Save and Get Prices
													</Button>
												</>
											)}
										</Stack>
									)}
								</Stack>
							)}
						</Stack>
					</Box>
				</>
			)}

			{/* Scrollable Content */}
			<Box
				sx={{
					flex: 1,
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'column',
					px: { xs: 3, sm: 4, md: 3, lg: 2.5, xl: 2 },
					pb: 3,
				}}
			>
				<Outlet />
			</Box>
		</Box>
	);
};

export const QuotesPage = () => {
	const { company } = useGlobalState();

	return company ? (
		<Routes>
			<Route path="/" element={<QuotesGridList />} />
			<Route
				path=":quoteId/*"
				element={
					<QuoteStateProvider>
						<QuoteFetchWrapper>
							<DeckProviderWrapper>
								<DesignSpanProvider>
									<QuoteLayout />
								</DesignSpanProvider>
							</DeckProviderWrapper>
						</QuoteFetchWrapper>
					</QuoteStateProvider>
				}
			>
				<Route path="options" element={<OptionSelectionWrapper />} />
				<Route path="pricing" element={<Pricing />} />
				<Route path="proposal" element={<ProposalPage />} />
				<Route path="preview" element={<ProposalPreview />} />
				<Route path="*" element={<OptionSelectionWrapper />} />
			</Route>
		</Routes>
	) : (
		<></>
	);
};
